import type { ReactElement } from 'react';
import { memo, useMemo } from 'react';

import shouldForwardProp from '@styled-system/should-forward-prop';
import add from 'lodash/fp/add';
import first from 'lodash/fp/first';
import styled, { css } from 'styled-components';

import MuiAvatar from '@mui/material/Avatar';

import type { Company as AdminCompany } from '../../../schema/admin';
import type { Company as UserCompany } from '../../../schema/user';
import { isNotNil } from '../../../services/guards';
import palette from '../../../theme/palette';

import Header from '../Header';

type Company = AdminCompany | UserCompany

const colors = [
  palette.avatars.marguerite,
  palette.avatars.paradiso,
  palette.avatars.cerise,
  palette.avatars.sharon,
  palette.avatars.saintTropez,
  palette.avatars.corn,
];

export const variants = {
  md: {
    headerSize: 'h5',
    size: '4rem',
  },
  md_s: {
    headerSize: 'h6',
    size: '3.2rem',
  },
  sm: {
    headerSize: 'h7',
    size: '2.4rem',
  },
  xs: {
    headerSize: 'h7',
    size: '2rem',
  },
} as const;

type Variant = keyof typeof variants;

type AvatarProps = Readonly<{
  sizeVariant: Variant;
  colorIndex: number;
  outlined: boolean;
  disabled: boolean;
}>;

const avatarSizes = (props: AvatarProps) => {
  const { size } = variants[props.sizeVariant];

  return css`
    height: ${size};
    width: ${size};
  `;
};

const avatarColors = (props: AvatarProps) => css`
  background-color: ${colors[props.colorIndex]};
  ${props.disabled && css`
    filter: grayscale(1);
  `}
`;

const avatarOutline = (props: AvatarProps) => props.outlined && css`
  outline: ${({ theme }) => theme.scTheme.borders.styles.medium.inverted.white};
`;

const StyledAvatar = styled(MuiAvatar).withConfig({
  shouldForwardProp: (prop) => shouldForwardProp(prop),
})<AvatarProps>`
  &&& {
    align-items: center;
    border-radius: ${({ theme }) => theme.scTheme.borders.radii.md};
    display: flex;
    ${avatarColors}
    ${avatarSizes}
    ${avatarOutline}
    text-transform: uppercase;

    &:has(.MuiAvatar-img) {
      background-color: transparent;
    }
  }
`;

const toImageSrc = (logo: string): string => {
  if (logo.startsWith('data:') || logo.startsWith('http') || logo.startsWith('blob:')) {
    return logo;
  }
  return `data:image/png;base64,${logo}`;
};

const charToCode = (char: string) => char.charCodeAt(0);

const getInitials = (name: string): string => {
  const words = name.trim().split(/[^a-zA-Z]+/).filter(Boolean);
  const firstInitial = first(words[0]) ?? '';
  const secondInitial = words.length > 1 ? first(words[1]) ?? '' : '';
  return `${firstInitial}${secondInitial}`;
};

export type Props = Readonly<{
  company: Pick<Company, 'logo' | 'name'> & ({ id?: string });
  variant?: Variant;
  outlined?: boolean;
  disabled?: boolean;
}>;

const CompanyAvatar = memo(({
  company, variant = 'md', outlined = false, disabled = false,
}: Props): ReactElement => {
  const colorIndex = useMemo(() => (company?.id ?? company.name)
    .split('')
    .map(charToCode)
    .reduce(add, 0) % colors.length, [company.id, company.name]);

  const src = useMemo(
    () => (isNotNil(company.logo) ? toImageSrc(company.logo) : undefined),
    [company.logo],
  );

  return (
    <StyledAvatar
      disabled={disabled}
      sizeVariant={variant}
      colorIndex={colorIndex}
      src={src}
      alt={company.name}
      outlined={outlined}
    >
      <Header as={variants[variant].headerSize} inverted>
        {getInitials(company.name)}
      </Header>
    </StyledAvatar>
  );
});

export default CompanyAvatar;
